import graphql from "babel-plugin-relay/macro";
import { Suspense, useEffect } from "react";
import {
  PreloadedQuery,
  useFragment,
  usePreloadedQuery,
  useQueryLoader,
} from "react-relay";

import { Loader } from "@whoosh/design-system/src/components/display";

import { LessonTypeSelect } from "../../../components/LessonTypeSelect/LessonTypeSelect";
import { InstructorLessonTypeSelectQuery } from "./__generated__/InstructorLessonTypeSelectQuery.graphql";
import {
  InstructorLessonTypeSelect_session$data,
  InstructorLessonTypeSelect_session$key,
} from "./__generated__/InstructorLessonTypeSelect_session.graphql";

type Props = {
  sessionRef: InstructorLessonTypeSelect_session$key | null;
  onChange: (lessonTypeId: string, duration: number) => void;
  duration: number | undefined;
  lessonTypeId: string | undefined;
  facilitySlug: string;
  instructorId: string;
  testID?: string;
};
export const InstructorLessonTypeSelect = ({
  sessionRef,
  onChange,
  duration,
  lessonTypeId,
  facilitySlug,
  instructorId,
  testID,
}: Props) => {
  const session = useFragment(
    InstructorLessonTypeSelectSessionFragmentString,
    sessionRef
  );
  const [queryRef, loadQuery] = useQueryLoader<InstructorLessonTypeSelectQuery>(
    InstructorLessonTypeSelectQueryString
  );

  const membershipTypeId =
    session?.currentClientProfile?.member?.membershipType?.id;

  useEffect(() => {
    if (instructorId && membershipTypeId && facilitySlug) {
      loadQuery(
        { instructorId, membershipTypeId, facilitySlug },
        { fetchPolicy: "store-and-network" }
      );
    }
  }, [facilitySlug, instructorId, loadQuery, membershipTypeId]);

  return (
    <Suspense fallback={<Loader />}>
      {queryRef ? (
        <InstructorLessonTypeSelectInner
          queryRef={queryRef}
          onChange={onChange}
          lessonTypeId={lessonTypeId}
          duration={duration}
          session={session ?? null}
          testID={testID}
        />
      ) : null}
    </Suspense>
  );
};

type InnerProps = {
  queryRef: PreloadedQuery<InstructorLessonTypeSelectQuery>;
  onChange: (lessonTypeId: string, duration: number) => void;
  duration: number | undefined;
  lessonTypeId: string | undefined;
  session: InstructorLessonTypeSelect_session$data | null;
  testID?: string;
};

const InstructorLessonTypeSelectInner = ({
  duration,
  lessonTypeId,
  onChange,
  queryRef,
  session,
  testID,
}: InnerProps) => {
  const data = usePreloadedQuery(
    InstructorLessonTypeSelectQueryString,
    queryRef
  );

  return (
    <LessonTypeSelect
      instructorRef={data.node ?? null}
      facilityRef={data.facility ?? null}
      sessionRef={session ?? null}
      onChange={onChange}
      selectedDuration={duration}
      selectedLessonTypeId={lessonTypeId}
      testID={testID}
    />
  );
};

const InstructorLessonTypeSelectSessionFragmentString = graphql`
  fragment InstructorLessonTypeSelect_session on Session {
    ...LessonTypeSelect_session
    currentClientProfile {
      member {
        membershipType {
          id
        }
      }
    }
  }
`;

const InstructorLessonTypeSelectQueryString = graphql`
  query InstructorLessonTypeSelectQuery(
    $instructorId: ID!
    $membershipTypeId: ID
    $facilitySlug: String!
  ) {
    node(id: $instructorId) {
      ...LessonTypeSelect_staffProfile
    }
    facility(slug: $facilitySlug) {
      ...LessonTypeSelect_facility
    }
  }
`;
